/**
 * DocumentsDashboard Component
 * Main dashboard for document management with search, statistics, and quick actions
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  Typography,
  Button,
  IconButton,
  Chip,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Upload as UploadIcon,
  Assessment as AssessmentIcon,
  Description as DocumentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import {
  documentsService,
  DocumentStatistics,
  Document
} from '../../services/documentsService';
import { useSiteContext } from '../../hooks/useSiteContext';
import DocumentSearch from '../../components/Documents/DocumentSearch';
import DocumentViewer from '../../components/Documents/DocumentViewer';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`documents-tabpanel-${index}`}
      aria-labelledby={`documents-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const DocumentsDashboard: React.FC = () => {
  const { currentSite } = useSiteContext();
  const [tabValue, setTabValue] = useState(0);
  const [statistics, setStatistics] = useState<DocumentStatistics | null>(null);
  const [recentDocuments, setRecentDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Document viewer state
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);

  const handleDocumentSelect = (document: Document) => {
    setSelectedDocument(document);
    setDocumentViewerOpen(true);
  };

  const handleCloseDocumentViewer = () => {
    setDocumentViewerOpen(false);
    setSelectedDocument(null);
  };

  useEffect(() => {
    loadDashboardData();
  }, [currentSite.siteId]);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [statsData, docsResponse] = await Promise.all([
        documentsService.getDocumentStatistics(),
        documentsService.getDocuments({
          site: currentSite.siteId || undefined,
          page_size: 5,
          ordering: '-created_date'
        })
      ]);
      
      setStatistics(statsData);
      setRecentDocuments(docsResponse.results);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const StatCard: React.FC<{
    title: string;
    value: number;
    icon: React.ReactNode;
    color: string;
    subtitle?: string;
  }> = ({ title, value, icon, color, subtitle }) => (
    <Card sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ color, fontSize: '2rem' }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" component="div" sx={{ color, fontWeight: 'bold' }}>
            {value.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );

  const CategoryCard: React.FC<{
    title: string;
    count: number;
    total: number;
  }> = ({ title, count, total }) => {
    const percentage = total > 0 ? (count / total) * 100 : 0;
    
    return (
      <Card sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {count}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            documents
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={percentage} 
          sx={{ height: 8, borderRadius: 4 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {percentage.toFixed(1)}% of total
        </Typography>
      </Card>
    );
  };

  if (loading && !statistics) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Document Management
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Document Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            color="primary"
          >
            Upload Document
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
          >
            New Document
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Overview */}
      {statistics && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Documents"
              value={statistics.total_documents}
              icon={<DocumentIcon />}
              color="#1976d2"
              subtitle={`for ${currentSite.siteName || 'current site'}`}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Need Review"
              value={statistics.needs_review_count}
              icon={<ScheduleIcon />}
              color="#f57c00"
              subtitle="documents requiring review"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Expired"
              value={statistics.expired_count}
              icon={<WarningIcon />}
              color="#d32f2f"
              subtitle="documents past expiry date"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Published"
              value={statistics.by_status['Published'] || 0}
              icon={<CheckCircleIcon />}
              color="#388e3c"
              subtitle="published documents"
            />
          </Grid>
        </Grid>
      )}

      {/* Status and Category Breakdown */}
      {statistics && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Documents by Status
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(statistics.by_status || {}).map(([status, count]) => (
                  <Grid item xs={6} key={status}>
                    <Box sx={{ textAlign: 'center', p: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {count}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {status}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Documents by Category
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(statistics.by_category || {}).slice(0, 4).map(([category, count]) => (
                  <Grid item xs={12} key={category}>
                    <CategoryCard
                      title={category}
                      count={count}
                      total={statistics.total_documents}
                    />
                  </Grid>
                ))}
              </Grid>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Recent Documents and Search */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab 
              label="Recent Documents" 
              icon={<DocumentIcon />}
              iconPosition="start"
            />
            <Tab 
              label="Search Documents" 
              icon={<SearchIcon />}
              iconPosition="start"
            />
            <Tab 
              label="Analytics" 
              icon={<AssessmentIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Box>
        
        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Recently Added Documents
          </Typography>
          {recentDocuments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No recent documents found
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {recentDocuments.map((doc) => (
                <Grid item xs={12} key={doc.id}>
                  <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <DocumentIcon color="primary" />
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        variant="body1" 
                        fontWeight="medium"
                        sx={{ 
                          cursor: 'pointer',
                          color: 'primary.main',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                        onClick={() => handleDocumentSelect(doc)}
                      >
                        {doc.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {doc.document_type_name} • {doc.document_number} • v{doc.version}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Created {new Date(doc.created_date).toLocaleDateString()} by {doc.created_by_name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip 
                        label={doc.status} 
                        size="small" 
                        color={
                          doc.status === 'published' ? 'success' :
                          doc.status === 'approved' ? 'info' :
                          doc.status === 'under_review' ? 'warning' : 'default'
                        }
                      />
                      {doc.is_expired && (
                        <Chip label="Expired" size="small" color="error" />
                      )}
                      {doc.needs_review && (
                        <Chip label="Needs Review" size="small" color="warning" />
                      )}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <DocumentSearch onDocumentSelect={handleDocumentSelect} />
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Document Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Analytics and reporting features coming soon...
          </Typography>
          
          {statistics && (
            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Access Level Distribution
                  </Typography>
                  {Object.entries(statistics.by_access_level || {}).map(([level, count]) => (
                    <Box key={level} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">{level}</Typography>
                      <Typography variant="body2" fontWeight="bold">{count}</Typography>
                    </Box>
                  ))}
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Document Health
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Current Documents</Typography>
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      {statistics.total_documents - statistics.expired_count}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Expired Documents</Typography>
                    <Typography variant="body2" fontWeight="bold" color="error.main">
                      {statistics.expired_count}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Need Review</Typography>
                    <Typography variant="body2" fontWeight="bold" color="warning.main">
                      {statistics.needs_review_count}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            </Grid>
          )}
        </TabPanel>
      </Card>

      {/* Document Viewer Dialog */}
      <DocumentViewer
        document={selectedDocument}
        open={documentViewerOpen}
        onClose={handleCloseDocumentViewer}
      />
    </Box>
  );
};

export default DocumentsDashboard; 